"use client"

import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"

import { LoadingOverlay } from "@/components/LoadingOverlay"
import { Button } from "@/components/ui/Button"
import { Link } from "@/components/ui/Link"
import { PlanBadge } from "@/metrics/teachers/PlanBadge"
import { StudentWorkInfoIcon } from "@/components/icons/StudentWorkInfoIcon"

import { SchoolsMetricsPageController } from "./SchoolsMetricsPageController"
import Organization from "../../../app/model/domain/organization"
import {
  OrganizationMetricsData,
  OrganizationUsageStats,
} from "../../../app/model/domain/organization-usage-stats"
import { DataTable } from "../DataTable"

const columnHelper = createColumnHelper<OrganizationMetricsData>()

export interface SchoolsMetricsPageProps {
  controller: SchoolsMetricsPageController
}

export function SchoolsMetricsPage({ controller }: SchoolsMetricsPageProps) {
  const {
    data: organizationsMetricsData,
    isPending: isOrganizationsMetricsDataPending,
    isError: isOrganizationsMetricsDataError,
  } = useQuery({
    queryKey: [
      controller.constructor.name,
      controller.getOrganizationsMetricsData.name,
    ],
    queryFn: () => controller.getOrganizationsMetricsData(),
  })

  const [isLoadingOverlayVisible, setIsLoadingOverlayVisible] = useState(false)

  async function handleShareClick(
    organizationId: string,
    usageStats: OrganizationUsageStats,
  ): Promise<void> {
    setIsLoadingOverlayVisible(true)
    const order = await controller.getActiveOrder(organizationId)
    controller.shareMetrics(organizationId, usageStats, order)
    setIsLoadingOverlayVisible(false)
  }

  function handleInviteClick(organization: Organization) {
    controller.inviteUsers(organization)
  }

  function handleNudgeClick() {
    controller.nudgeTeachers(undefined)
  }

  function handleRowClick(row: OrganizationMetricsData) {
    controller.goToTeachersMetricsPage(row.organization.id)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<OrganizationMetricsData, any>[] = [
    columnHelper.accessor("organization.name", {
      id: "name",
      header: "School",
    }),
    columnHelper.accessor("isPro", {
      id: "isPro",
      header: "Plan",
      cell: ({ getValue }) => <PlanBadge isPro={getValue()} />,
    }),
    columnHelper.accessor("stats.teachersActiveThisYear", {
      id: "teachersActiveThisYear",
      header: "Active teachers",
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),
    columnHelper.accessor("stats.assignmentsWorkedOnThisYear", {
      id: "assignmentsWorkedOnThisYear",
      header: "Assignments worked",
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),
    columnHelper.accessor("stats.pagesWorkedOnThisYear", {
      id: "pagesWorkedOnThisYear",
      header: () => {
        return (
          <>
            Student Work Units <StudentWorkInfoIcon />
          </>
        )
      },
      sortUndefined: -1,
      cell: ({ getValue }) => getValue()?.toLocaleString() ?? "–",
    }),
    columnHelper.accessor("stats.approximateSavings", {
      id: "approximateSavings",
      header: "Savings on ink, copies, paper",
      sortUndefined: -1,
      cell: ({ row }) => row.original.stats.approximateSavingsDisplay ?? "–",
    }),
    columnHelper.display({
      header: "Share",
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const organizationId = row.original.organization.id
            const usageStats = row.original.stats
            return handleShareClick(organizationId, usageStats)
          }}
        >
          Share
        </Button>
      ),
    }),
    columnHelper.display({
      header: "Invite",
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const organization = row.original.organization
            handleInviteClick(organization)
          }}
        >
          Invite
        </Button>
      ),
    }),
  ]

  const isUserAdminOfAnyContract = controller.isUserAdminOfAnyContract()

  // Legacy Refactor: The height calculation on the topmost element accounts for:
  //   - 60px Metrics header
  //   - Zoom of 1.6 on .react-container in src/index.css
  // TODO: find a way to add this to tailwind config
  return (
    <div className="tw-h-[calc((100vh-60px)/1.6)] tw-flex tw-flex-col tw-p-[1.4em]">
      <div className="tw-mb-4 tw-flex tw-justify-between">
        <div className="tw-my-auto">
          <div className="tw-whitespace-nowrap">
            <h2 className="tw-inline">All schools</h2>
            <span className="tw-ml-4 tw-text-sm tw-text-subtle">
              {controller.statsTimePeriod} - Present
            </span>
          </div>

          <Link href="/account/profile?tab=school">Manage organizations</Link>
        </div>

        {isUserAdminOfAnyContract && (
          <Button onClick={handleNudgeClick}>Nudge My Teachers</Button>
        )}
      </div>

      {isOrganizationsMetricsDataPending && (
        <p className="tw-text-sm">Loading...</p>
      )}

      {isOrganizationsMetricsDataError && (
        <p className="tw-text-sm tw-text-red-500">{controller.errorMessage}</p>
      )}

      {organizationsMetricsData &&
        !isOrganizationsMetricsDataPending &&
        !isOrganizationsMetricsDataError && (
          <DataTable
            columns={columns}
            data={organizationsMetricsData}
            initialState={{
              sorting: [{ id: "pagesWorkedOnThisYear", desc: true }],
            }}
            onRowClick={handleRowClick}
          />
        )}

      {isLoadingOverlayVisible && <LoadingOverlay />}
    </div>
  )
}
